import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../student/Navbar';
import AdminSidebar from './AdminSidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import { FaEye, FaChartLine, FaUsers, FaClock, FaSearch, FaFilter, FaDownload } from 'react-icons/fa';

// Website color palette
const websiteColorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

const StudentActivityDashboard = () => {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('week');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [deanView, setDeanView] = useState(false);
  const [showAnalyticsDetails, setShowAnalyticsDetails] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchStudentAnalytics();
      fetchActivityLogs();
    }
  }, [selectedStudent, timeRange, currentPage, filterAction]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data || []);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentAnalytics = async () => {
    if (!selectedStudent) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/student-tracking/student-activity/${selectedStudent.studentId}?timeRange=${timeRange}&importantOnly=${deanView}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    if (!selectedStudent) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/student-tracking/student-logs/${selectedStudent.studentId}?page=${currentPage}&limit=20&action=${filterAction}&importantOnly=${deanView}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActivityLogs(response.data.activities || []);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError('Failed to load activity logs');
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setCurrentPage(1);
    setSearchTerm('');
    setFilterAction('');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'page_view': return '👁️';
      case 'button_click': return '🖱️';
      case 'form_interaction': return '📝';
      case 'data_operation': return '💾';
      case 'search': return '🔍';
      case 'file_operation': return '📁';
      case 'error': return '❌';
      case 'modal_interaction': return '🪟';
      case 'navigation': return '🧭';
      case 'record_access': return '🛡️';
      case 'auth_event': return '🔐';
      case 'security_event': return '🚨';
      case 'login': return '🔐';
      case 'logout': return '🚪';
      case 'profile_update': return '👤';
      case 'appointment_created': return '📅';
      case 'appointment_updated': return '✏️';
      case 'appointment_cancelled': return '❌';
      case 'patient_added': return '➕';
      case 'patient_updated': return '✏️';
      case 'chart_updated': return '🦷';
      case 'review_submitted': return '⭐';
      default: return '📊';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'page_view': return 'bg-blue-100 text-blue-800';
      case 'button_click': return 'bg-green-100 text-green-800';
      case 'form_interaction': return 'bg-yellow-100 text-yellow-800';
      case 'data_operation': return 'bg-purple-100 text-purple-800';
      case 'search': return 'bg-indigo-100 text-indigo-800';
      case 'file_operation': return 'bg-orange-100 text-orange-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'modal_interaction': return 'bg-pink-100 text-pink-800';
      case 'navigation': return 'bg-cyan-100 text-cyan-800';
      case 'record_access': return 'bg-blue-100 text-blue-900';
      case 'auth_event': return 'bg-teal-100 text-teal-800';
      case 'security_event': return 'bg-red-200 text-red-900';
      case 'login': return 'bg-green-100 text-green-800';
      case 'logout': return 'bg-gray-100 text-gray-800';
      case 'profile_update': return 'bg-blue-100 text-blue-800';
      case 'appointment_created': return 'bg-green-100 text-green-800';
      case 'appointment_updated': return 'bg-yellow-100 text-yellow-800';
      case 'appointment_cancelled': return 'bg-red-100 text-red-800';
      case 'patient_added': return 'bg-green-100 text-green-800';
      case 'patient_updated': return 'bg-yellow-100 text-yellow-800';
      case 'chart_updated': return 'bg-purple-100 text-purple-800';
      case 'review_submitted': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHumanReadableDescription = (log) => {
    const action = log.action;
    let details = {};
    let metadata = {};
    
    try {
      details = typeof log.details === 'string' ? JSON.parse(log.details) : log.details || {};
    } catch (e) {
      details = { raw: log.details };
    }
    
    try {
      metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata || {};
    } catch (e) {
      metadata = { raw: log.metadata };
    }

    switch (action) {
      case 'page_view':
        return `Viewed page: ${details.page || details.raw || 'Unknown page'}`;
      case 'button_click':
        return `Clicked button: ${details.button || details.element || 'Unknown button'}`;
      case 'form_interaction':
        return `Form interaction: ${details.form || details.field || 'Form submitted'}`;
      case 'data_operation':
        return `Data ${details.operation || 'operation'} on ${details.entityType || 'entity'}${details.entityId ? ' (' + details.entityId + ')' : ''}`;
      case 'search':
        return `Searched for: "${details.query || details.searchTerm || 'Unknown query'}"`;
      case 'file_operation':
        return `File operation: ${details.fileName || details.operation || 'File processed'}`;
      case 'error':
        return `Error occurred: ${details.error || details.message || 'Unknown error'}`;
      case 'modal_interaction':
        return `Modal interaction: ${details.modal || 'Modal opened/closed'}`;
      case 'navigation':
        return `Navigated to: ${details.route || details.page || 'New page'}`;
      case 'record_access':
        return `Viewed ${details.entityType || 'record'} ${details.entityId ? '(' + details.entityId + ')' : ''}${details.purpose ? ' for ' + details.purpose : ''}`;
      case 'auth_event': {
        const type = details.eventType || 'auth event';
        if (type === 'login') return 'Logged in';
        if (type === 'logout') return 'Logged out';
        return `Authentication event: ${type}`;
      }
      case 'security_event': {
        const type = details.eventType || 'security event';
        if (type === 'ip_shared_across_accounts') return 'Security alert: Same IP used across multiple accounts';
        return `Security alert: ${type}`;
      }
      case 'login':
        return `Logged in successfully`;
      case 'logout':
        return `Logged out`;
      case 'profile_update':
        return `Updated profile information`;
      case 'appointment_created':
        return `Created appointment${details.patientName ? ` for ${details.patientName}` : ''}`;
      case 'appointment_updated':
        return `Updated appointment${details.patientName ? ` for ${details.patientName}` : ''}`;
      case 'appointment_cancelled':
        return `Cancelled appointment${details.patientName ? ` for ${details.patientName}` : ''}`;
      case 'patient_added':
        return `Added new patient: ${details.patientName || 'Unknown patient'}`;
      case 'patient_updated':
        return `Updated patient: ${details.patientName || 'Unknown patient'}`;
      case 'chart_updated':
        return `Updated dental chart${details.patientName ? ` for ${details.patientName}` : ''}`;
      case 'review_submitted':
        return `Submitted review${details.rating ? ` (${details.rating} stars)` : ''}`;
      default:
        return `Performed action: ${action.replace('_', ' ')}`;
    }
  };

  // Derive last login/logout and last device/IP from logs
  const deriveSessionInfo = () => {
    if (!activityLogs || activityLogs.length === 0) return {};
    const sorted = [...activityLogs].sort((a, b) => {
      const aT = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const bT = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return bT - aT;
    });
    const parseEventType = (l, type) => {
      try {
        const d = typeof l.details === 'string' ? JSON.parse(l.details) : l.details;
        return d?.eventType === type;
      } catch { return false; }
    };
    const lastLogin = sorted.find(l => l.action === 'auth_event' && parseEventType(l, 'login'));
    const lastLogout = sorted.find(l => l.action === 'auth_event' && parseEventType(l, 'logout'));
    const withDevice = sorted.find(l => l.ipAddress || l.deviceOS || l.browser || l.deviceType) || sorted[0];
    const lastIp = withDevice?.ipAddress || 'N/A';
    const deviceOS = withDevice?.deviceOS || 'N/A';
    const browser = withDevice?.browser || 'N/A';
    const deviceType = withDevice?.deviceType || 'N/A';
    const parseTime = (t) => t ? (t.toDate ? t.toDate() : new Date(t)).toLocaleString() : 'N/A';
    return {
      lastLoginTime: lastLogin ? parseTime(lastLogin.timestamp) : 'N/A',
      lastLogoutTime: lastLogout ? parseTime(lastLogout.timestamp) : 'N/A',
      lastIp,
      deviceOS,
      browser,
      deviceType
    };
  };

  if (loading && !selectedStudent) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h1 className={`text-2xl sm:text-3xl font-bold text-[${websiteColorPalette.primary}] mb-2`}>
                Student Activity Dashboard
              </h1>
              <p className="text-gray-600">Monitor and analyze student activities and behavior</p>
            </motion.div>
            </div>


        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg"
          >
            <p className="text-red-700">{error}</p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Student Selection */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FaUsers className="mr-2 text-blue-600" />
                Select Student
              </h2>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {students.map((student) => (
                  <motion.button
                    key={student.studentId}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleStudentSelect(student)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedStudent?.studentId === student.studentId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{student.name}</div>
                    <div className="text-sm text-gray-500">ID: {student.studentId}</div>
                    <div className="text-sm text-gray-500">{student.email}</div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Analytics and Activity Logs */}
          <div className="lg:col-span-2">
            {selectedStudent ? (
              <>
                {/* Selected Student Info Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 p-6 mb-6"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                      <FaUsers className="mr-2 text-blue-600" />
                      Selected User
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Name</div>
                      <div className="text-gray-900 font-medium">{selectedStudent.name || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Student ID</div>
                      <div className="text-gray-900 font-medium">{selectedStudent.studentId || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="text-gray-900 font-medium">{selectedStudent.email || 'N/A'}</div>
                    </div>
                    {(() => {
                      const s = deriveSessionInfo();
                      return (
                        <>
                          <div>
                            <div className="text-sm text-gray-500">IP Address</div>
                            <div className="text-gray-900 font-medium">{s.lastIp}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">Device</div>
                            <div className="text-gray-900 font-medium">{s.deviceOS} • {s.browser} • {s.deviceType}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">Last Login</div>
                            <div className="text-gray-900 font-medium">{s.lastLoginTime}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">Last Logout</div>
                            <div className="text-gray-900 font-medium">{s.lastLogoutTime}</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </motion.div>
                {/* Analytics Overview */}
                {analytics && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 p-6 mb-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FaChartLine className="mr-2 text-green-600" />
                        Analytics for {selectedStudent.name}
                      </h2>
                      <div className="flex items-center space-x-3">
                        <label className="inline-flex items-center text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={deanView}
                            onChange={(e) => setDeanView(e.target.checked)}
                          />
                          Dean view (important only)
                        </label>
                        <select
                          value={timeRange}
                          onChange={(e) => setTimeRange(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="day">Last 24 Hours</option>
                          <option value="week">Last 7 Days</option>
                          <option value="month">Last 30 Days</option>
                          <option value="quarter">Last 3 Months</option>
                          <option value="year">Last Year</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{analytics.totalActivities || 0}</div>
                        <div className="text-sm text-gray-600">Total Activities</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{analytics.formInteractions || 0}</div>
                        <div className="text-sm text-gray-600">Form Interactions</div>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">{analytics.dataOperations || 0}</div>
                        <div className="text-sm text-gray-600">Data Operations</div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{analytics.errorCount || 0}</div>
                        <div className="text-sm text-gray-600">Errors</div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <button
                        onClick={() => setShowAnalyticsDetails(!showAnalyticsDetails)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        {showAnalyticsDetails ? 'Hide details' : 'Show details'}
                      </button>
                    </div>

                    {showAnalyticsDetails && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Most Active Pages */}
                        <div className="mb-2">
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Most Active Pages</h3>
                          <div className="space-y-2">
                            {analytics.mostActivePages && Array.isArray(analytics.mostActivePages) 
                              ? analytics.mostActivePages.slice(0, 5).map((page, index) => (
                                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                    <span className="text-sm font-medium">
                                      {page.page ? page.page.replace(/\//g, ' ').replace(/^\s+|\s+$/g, '') || 'Home' : 'Unknown Page'}
                                    </span>
                                    <span className="text-sm text-gray-600">{page.count || 0} visits</span>
                                  </div>
                                ))
                              : <div className="text-sm text-gray-500">No page data available</div>
                            }
                          </div>
                        </div>

                        {/* Most Common Actions */}
                        <div className="mb-2">
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Most Common Actions</h3>
                          <div className="space-y-2">
                            {analytics.mostCommonActions && Array.isArray(analytics.mostCommonActions)
                              ? analytics.mostCommonActions.slice(0, 5).map((action, index) => (
                                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                    <span className="text-sm font-medium">
                                      {action.action ? action.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown Action'}
                                    </span>
                                    <span className="text-sm text-gray-600">{action.count || 0} times</span>
                                  </div>
                                ))
                              : <div className="text-sm text-gray-500">No action data available</div>
                            }
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Activity Logs */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                      <FaClock className="mr-2 text-orange-600" />
                      Recent Activity
                    </h2>
                    <div className="flex space-x-2">
                      <select
                        value={filterAction}
                        onChange={(e) => setFilterAction(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">All Actions</option>
                        <option value="page_view">Page Views</option>
                        <option value="button_click">Button Clicks</option>
                        <option value="form_interaction">Form Interactions</option>
                        <option value="data_operation">Data Operations</option>
                        <option value="search">Searches</option>
                        <option value="file_operation">File Operations</option>
                        <option value="error">Errors</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {activityLogs.filter(l => !!l && !!l.action).map((log, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <span className="text-2xl">{getActionIcon(log.action)}</span>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                                  {log.action.replace('_', ' ').toUpperCase()}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {formatTimestamp(log.timestamp)}
                                </span>
                                {log.ipAddress && (
                                  <span className="text-xs text-gray-400">• IP {log.ipAddress}</span>
                                )}
                                {(log.deviceOS || log.browser) && (
                                  <span className="text-xs text-gray-400">• {log.deviceOS || 'OS'} / {log.browser || 'Browser'}</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-700">
                                {getHumanReadableDescription(log)}
                              </div>
                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div className="text-xs text-gray-500 mt-1">
                                  <details className="cursor-pointer">
                                    <summary className="hover:text-gray-700">Additional details</summary>
                                    <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                                      {(() => {
                                        try {
                                          const parsed = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
                                          // Render concise key info when available
                                          const importantKeys = ['entityType','entityId','operation','eventType','fileType','appointmentId','patientId'];
                                          const distilled = Object.fromEntries(Object.entries(parsed).filter(([k]) => importantKeys.includes(k)));
                                          const output = Object.keys(distilled).length ? distilled : parsed;
                                          return JSON.stringify(output, null, 2);
                                        } catch (e) {
                                          return log.metadata;
                                        }
                                      })()}
                                    </pre>
                                  </details>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {activityLogs.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No activity logs found for the selected criteria.
                    </div>
                  )}
                </motion.div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-lg shadow-sm p-12 text-center"
              >
                <FaEye className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Student</h3>
                <p className="text-gray-500">Choose a student from the list to view their activity analytics and logs.</p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default StudentActivityDashboard;

